import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";

interface DeleteRoutineDialogProps {
  onDelete: () => Promise<void>;
}

export const DeleteRoutineDialog = ({ onDelete }: DeleteRoutineDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    // Evitar que el AlertDialogAction cierre el modal antes de tiempo
    // y nos haga perder el estado de loading.
    e.preventDefault();
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await onDelete();
      // Cerramos el modal manualmente. La UI se actualiza vía cache
      // optimista, sin necesidad de recargar la página.
      setOpen(false);
    } catch (error) {
      console.error("Error deleting routine:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No se pudo eliminar la rutina. Por favor, intenta nuevamente.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="bg-primary dark:bg-white dark:hover:bg-gray-300
           border-white/20 hover:bg-[#FF8000] ocean:bg-[#081F2B]  sunset:bg-[#ff5500]  forest:bg-forest/90"
        >
          <Trash2 className="h-4 w-4 text-white  dark:text-black ocean:text-white ocean:hover:!text-black forest:text-white sunset:text-white" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta acción no se puede deshacer. La rutina será eliminada permanentemente.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-clubbers-red hover:bg-clubbers-red/90 sunset:bg-sunset forest:bg-forest ocean:bg-ocean"
          >
            {isDeleting ? "Eliminando..." : "Eliminar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
